import React, { useState, useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, Platform, StyleSheet } from 'react-native';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ADMIN_COLORS } from '@/constants/admin';
import type { AnchorRect } from '@/hooks/common/useAnchoredPosition';

export interface DropdownOption {
  value: string;
  label: string;
}

interface AdminSelectDropdownProps {
  label: string;
  options: DropdownOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  variant?: 'inline' | 'anchored';
}

export const AdminSelectDropdown: React.FC<AdminSelectDropdownProps> = ({
  label,
  options,
  selectedValue,
  onSelect,
  variant = 'inline',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const triggerRef = useRef<View>(null);

  const selectedLabel = options.find((o) => o.value === selectedValue)?.label ?? label;

  const toggle = useCallback(() => {
    if (variant === 'anchored') {
      if (isOpen) {
        setIsOpen(false);
      } else if (triggerRef.current) {
        triggerRef.current.measureInWindow((x, y, width, height) => {
          setAnchor({ x, y, w: width, h: height });
          setIsOpen(true);
        });
      }
    } else {
      setIsOpen((prev) => !prev);
    }
  }, [variant, isOpen]);

  const close = useCallback(() => setIsOpen(false), []);

  const handleSelect = useCallback(
    (value: string) => {
      onSelect(value);
      close();
    },
    [onSelect, close],
  );

  const renderMenuItems = () => (
    <View>
      {options.map((option) => (
        <TouchableOpacity
          key={option.value}
          style={styles.menuItem}
          onPress={() => handleSelect(option.value)}
          activeOpacity={0.7}
        >
          <Text
            style={[
              styles.menuItemText,
              selectedValue === option.value && styles.menuItemTextActive,
            ]}
          >
            {option.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  if (variant === 'anchored') {
    return (
      <>
        <View ref={triggerRef} style={styles.anchoredWrap}>
          <TouchableOpacity style={styles.anchoredToggle} onPress={toggle} activeOpacity={0.8}>
            <Text style={styles.anchoredToggleText}>{label}: {selectedLabel}</Text>
            <SVG_COMPONENTS.CHEVRON_DOWN width={12} height={12} color={ADMIN_COLORS.TEXT.SECONDARY} />
          </TouchableOpacity>
        </View>
        <AnchoredDropdown
          isOpen={isOpen}
          anchor={anchor}
          width={anchor?.w || 220}
          maxHeight={320}
          onRequestClose={close}
        >
          {renderMenuItems()}
        </AnchoredDropdown>
      </>
    );
  }

  return (
    <View style={styles.inlineContainer}>
      <TouchableOpacity style={styles.inlineToggle} onPress={toggle} activeOpacity={0.7}>
        <Text style={styles.inlineToggleText}>{selectedLabel}</Text>
        <View style={[styles.chevronContainer, isOpen && styles.chevronContainerRotated]}>
          <SVG_COMPONENTS.CHEVRON_DOWN width={16} height={16} color={ADMIN_COLORS.TEXT.SECONDARY} />
        </View>
      </TouchableOpacity>

      {isOpen && <View style={styles.inlineMenu}>{renderMenuItems()}</View>}
    </View>
  );
};

const styles = StyleSheet.create({
  // Inline variant (mobile)
  inlineContainer: {
    position: 'relative',
    paddingBottom: 8,
    zIndex: Platform.OS === 'web' ? 1001 : 100,
  },
  inlineToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    minHeight: 44,
  },
  inlineToggleText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    marginRight: 8,
  },
  chevronContainer: {
    transition: 'transform 0.2s',
  } as any,
  chevronContainerRotated: {
    transform: [{ rotate: '180deg' }],
  },
  inlineMenu: {
    position: 'absolute',
    top: 40,
    left: 0,
    right: 0,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 3,
    zIndex: 1000,
  },

  // Anchored variant (desktop)
  anchoredWrap: {
    alignSelf: 'flex-start',
  },
  anchoredToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  anchoredToggleText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    fontWeight: '500',
  },

  // Shared menu items
  menuItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  menuItemText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  menuItemTextActive: {
    fontWeight: '600',
    color: ADMIN_COLORS.PRIMARY,
  },
});
